import { Tooltip } from 'antd';
import React from 'react';
import {
    BLUE_100,
    BLUE_300,
    BLUE_500,
    BLUE_700,
    BLUE_900,
    GRAY_100,
} from '~styles/colors';
import type { ContestStats } from '../../../stores/contest';
import { WEEKDAY_LABELS, weekHourGrid } from './stats';
import style from './week-hour-heatmap.module.scss';

/** One hue, light to dark: a darker cell holds more entries. */
const STEPS = [BLUE_100, BLUE_300, BLUE_500, BLUE_700, BLUE_900];

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

function stepColor(entries: number, max: number): string {
    if (entries === 0 || max === 0) return GRAY_100;
    const index = Math.ceil((entries / max) * STEPS.length) - 1;
    return STEPS[Math.max(0, Math.min(STEPS.length - 1, index))];
}

function hourLabel(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Entries by weekday and hour of the day, in the contest's time zone, so the
 * busiest moments of the week stand out. Each cell names its slot on hover.
 */
export const WeekHourHeatmap = ({
    weekHours,
}: {
    weekHours: ContestStats['weekHours'];
}) => {
    const grid = weekHourGrid(weekHours);
    const max = Math.max(...grid.flat());
    if (max === 0) {
        return <p className={style.empty}>No entry yet.</p>;
    }
    return (
        <div className={style.heatmap}>
            <div className={style.grid}>
                <span />
                {HOURS.map((hour) => (
                    <span
                        key={hour}
                        className={style.hour_label}
                        aria-hidden="true"
                    >
                        {hour % 3 === 0 ? hour : ''}
                    </span>
                ))}
                {grid.map((hours, weekday) => (
                    <React.Fragment key={WEEKDAY_LABELS[weekday]}>
                        <span className={style.day_label}>
                            {WEEKDAY_LABELS[weekday]}
                        </span>
                        {hours.map((entries, hour) => (
                            <Tooltip
                                key={hour}
                                title={`${WEEKDAY_LABELS[weekday]} ${hourLabel(
                                    hour
                                )} to ${hourLabel(
                                    (hour + 1) % 24
                                )}: ${entries.toLocaleString()} ${
                                    entries === 1 ? 'entry' : 'entries'
                                }`}
                            >
                                <span
                                    role="img"
                                    className={style.cell}
                                    style={{
                                        background: stepColor(entries, max),
                                    }}
                                    aria-label={`${
                                        WEEKDAY_LABELS[weekday]
                                    } ${hourLabel(hour)}: ${entries}`}
                                />
                            </Tooltip>
                        ))}
                    </React.Fragment>
                ))}
            </div>
            <div className={style.scale}>
                <span>Fewer</span>
                {STEPS.map((color) => (
                    <span
                        key={color}
                        className={style.scale_step}
                        style={{ background: color }}
                    />
                ))}
                <span>More</span>
            </div>
        </div>
    );
};
